import { useState, useRef, ChangeEvent, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { notify } from "@/hooks/useNotify";
import { Upload, X, Edit } from "lucide-react";

interface AdvancedLogoUploadProps {
  onImageUpload: (image: string | null) => void;
  initialImage?: string;
  className?: string;
  disabled?: boolean;
}

interface ImageState {
  previewUrl: string | null;
  isEditing: boolean;
}

const AdvancedLogoUpload = ({ 
  onImageUpload, 
  initialImage, 
  className = "",
  disabled = false
}: AdvancedLogoUploadProps) => {
  const [imageState, setImageState] = useState<ImageState>({
    previewUrl: null,
    isEditing: false
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const originalImageRef = useRef<HTMLImageElement | null>(null);

  // Initialize with existing image if provided
  useEffect(() => {
    if (initialImage) {
      setImageState(prev => ({ ...prev, previewUrl: initialImage }));
      const img = new Image();
      img.onload = () => {
        originalImageRef.current = img;
        updatePreviewCanvas(img);
      };
      img.crossOrigin = "anonymous";
      img.src = initialImage;
    }
  }, [initialImage]);

  // Update preview canvas
  const updatePreviewCanvas = useCallback((img: HTMLImageElement) => {
    if (!previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to a reasonable display size
    const maxDisplaySize = 320;
    const imgAspect = img.width / img.height;
    
    let displayWidth, displayHeight;
    if (imgAspect > 1) {
      displayWidth = maxDisplaySize;
      displayHeight = maxDisplaySize / imgAspect;
    } else {
      displayHeight = maxDisplaySize;
      displayWidth = maxDisplaySize * imgAspect;
    }

    canvas.width = displayWidth;
    canvas.height = displayHeight;

    // Clear and draw the image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

    // Convert to data URL and pass to parent
    const dataUrl = canvas.toDataURL('image/png');
    onImageUpload(dataUrl);
  }, [onImageUpload]);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      notify.error("Please upload an image file (JPEG, PNG, GIF, or SVG).");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      notify.error("Image size should be less than 2MB");
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setImageState(prev => ({ ...prev, previewUrl: result, isEditing: true }));
      
      // Create image element to process
      const img = new Image();
      img.onload = () => {
        originalImageRef.current = img;
        updatePreviewCanvas(img);
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    if (disabled) return;
    
    setImageState({
      previewUrl: null,
      isEditing: false
    });
    originalImageRef.current = null;
    onImageUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEditExisting = () => {
    if (disabled) return;
    
    setImageState(prev => ({ ...prev, isEditing: true }));
  };

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageChange}
        accept="image/jpeg, image/png, image/gif, image/svg+xml"
        className="sr-only"
        aria-label="Upload company logo"
        disabled={disabled}
      />
      
      {imageState.previewUrl ? (
        <div className="space-y-4">
          <div className="relative border rounded-lg overflow-hidden bg-white">
            <div className="relative">
              <canvas
                ref={previewCanvasRef}
                className="object-contain"
                style={{ maxWidth: '400px', maxHeight: '300px' }}
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
                  aria-label="Remove logo"
                >
                  <X size={18} className="text-gray-700" />
                </button>
              )}
              {!imageState.isEditing && !disabled && (
                <button
                  type="button"
                  onClick={handleEditExisting}
                  className="absolute top-2 left-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
                  aria-label="Edit logo"
                >
                  <Edit size={18} className="text-gray-700" />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-80 h-45 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center p-4 bg-gray-50" style={{ width: '320px', height: '180px' }}>
          <Upload size={32} className="text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 text-center">Upload your company logo</p>
          <p className="text-xs text-gray-400 text-center mt-1">Any aspect ratio supported</p>
        </div>
      )}
      
      <Button
        type="button"
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        className="mt-2"
        disabled={disabled}
      >
        {imageState.previewUrl ? "Change Logo" : "Upload Logo"}
      </Button>
    </div>
  );
};

export default AdvancedLogoUpload;
